import React, {
  Children,
  cloneElement,
  isValidElement,
  ReactElement,
  ReactNode,
  useRef,
  useState
} from 'react'
import {
  FloatingPortal,
  useClick,
  useDismiss,
  useFloating,
  useFocus,
  useHover,
  useInteractions,
  safePolygon,
  useListNavigation,
  flip,
  offset,
  type Placement
} from '@floating-ui/react'
import styled from 'styled-components'
import { AnimatePresence, motion } from 'framer-motion'
import { ButtonProps } from '@components/Button'
import { ButtonGroup } from '@components/Button/ButtonGroup'
import { callAll } from '@utils'

type ButtonElement = ReactElement<ButtonProps>
type DropdownItem = ButtonElement | ReactElement<{ children?: ReactNode; label?: ReactNode }>

interface DropdownMenuSectionProps {
  label?: ReactNode
  children?: ReactNode
}

export const DropdownMenuSection: React.FC<DropdownMenuSectionProps> = ({ children }) => (
  <>{children}</>
)

DropdownMenuSection.displayName = 'DropdownMenuSection'

interface Props {
  children: ButtonElement
  items: DropdownItem[]
  placement?: Placement
  header?: ReactNode
  closeOnItemClick?: boolean
}

const isButtonGroup = (item: DropdownItem): boolean => {
  if (item.type === ButtonGroup) return true
  return (
    typeof item.type !== 'string' &&
    (item.type as { displayName?: string }).displayName === 'ButtonGroup'
  )
}

const isDropdownMenuSection = (item: DropdownItem): boolean => {
  if (item.type === DropdownMenuSection) return true
  return (
    typeof item.type !== 'string' &&
    (item.type as { displayName?: string }).displayName === 'DropdownMenuSection'
  )
}

const getGroupButtons = (item: DropdownItem): ButtonElement[] => {
  if (isButtonGroup(item) || isDropdownMenuSection(item)) {
    return Children.toArray(item.props.children).filter(isValidElement) as ButtonElement[]
  }

  return [item as ButtonElement]
}

const getSectionLabel = (item: DropdownItem): ReactNode | undefined => {
  if (!isDropdownMenuSection(item)) return undefined
  return (item.props as DropdownMenuSectionProps).label
}

export const DropdownMenu: React.FC<Props> = ({
  children,
  items,
  placement = 'bottom-end',
  header,
  closeOnItemClick = true
}) => {
  const [activeIndex, setActiveIndex] = useState<number | null>(null)
  const [isVisible, setVisible] = useState(false)

  const { context, refs, floatingStyles } = useFloating({
    open: isVisible,
    onOpenChange: setVisible,
    placement,
    middleware: [offset(8), flip()]
  })
  const listRef = useRef<(HTMLButtonElement | null)[]>([])

  const dismiss = useDismiss(context, { outsidePress: true })
  const click = useClick(context)
  const hover = useHover(context, { handleClose: safePolygon() })
  const focus = useFocus(context)

  const listNavigation = useListNavigation(context, {
    listRef,
    activeIndex,
    onNavigate: setActiveIndex
  })

  const { getReferenceProps, getFloatingProps } = useInteractions([
    click,
    focus,
    hover,
    dismiss,
    listNavigation
  ])

  let buttonIndex = 0

  return (
    <>
      {cloneElement(children, {
        ref: refs.setReference,
        ...getReferenceProps()
      })}
      <FloatingPortal>
        <AnimatePresence mode="wait">
          {isVisible && (
            <StyledDropdownMenu
              ref={refs.setFloating}
              style={floatingStyles}
              {...getFloatingProps()}
            >
              {header && <MenuHeader>{header}</MenuHeader>}
              {items.map((item, groupIndex) => {
                const groupButtons = getGroupButtons(item)
                const sectionLabel = getSectionLabel(item)

                return (
                  <AnimatedListItem key={`list-group-${groupIndex}`}>
                    {sectionLabel && <SectionLabel>{sectionLabel}</SectionLabel>}
                    {groupButtons.map(Child => {
                      const index = buttonIndex++

                      return cloneElement(Child, {
                        key: Child.key ?? `list-item-${index}`,
                        onClick: callAll(
                          Child.props.onClick,
                          closeOnItemClick ? () => setVisible(false) : undefined
                        ),
                        tabIndex: activeIndex === index ? 0 : -1,
                        isSecondary: Child.props.isSecondary ?? true,
                        ref: (el: HTMLButtonElement) => {
                          listRef.current[index] = el
                        }
                      })
                    })}
                  </AnimatedListItem>
                )
              })}
            </StyledDropdownMenu>
          )}
        </AnimatePresence>
      </FloatingPortal>
    </>
  )
}

const StyledDropdownMenu = styled(motion.div).attrs({
  variants: {
    open: {
      opacity: 1
    },
    closed: {
      opacity: 0
    }
  },
  initial: 'closed',
  animate: 'open',
  exit: 'closed'
})`
  display: flex;
  flex-direction: column;
  gap: 1px;
  border-radius: ${({ theme }) => theme.borderRadius.md}rem;
  position: absolute;
  min-width: 200px;
  box-shadow: ${({ theme }) => theme.shadow.md};
  background: ${({ theme }) => theme.colors.neutralGray};

  z-index: 9999;
`

const MenuHeader = styled.div`
  background: ${({ theme }) => theme.colors.neutralWhite};
  border-radius: ${({ theme }) => theme.borderRadius.md}rem
    ${({ theme }) => theme.borderRadius.md}rem 0 0;
  padding: ${({ theme }) => theme.spacing.sm}rem ${({ theme }) => theme.spacing.md}rem;
  font-size: ${({ theme }) => theme.fontSize.px11}rem;
  font-weight: 700;
  letter-spacing: 1px;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.iconGray};
`

const SectionLabel = styled(MenuHeader)`
  border-radius: 0;
`

const AnimatedListItem = styled.div`
  display: flex;
  flex-direction: column;
  background: ${({ theme }) => theme.colors.neutralWhite};
  border-radius: ${({ theme }) => theme.borderRadius.md}rem;

  button {
    width: 100%;
  }

  button:not(:first-of-type) {
    border-top-left-radius: 0;
    border-top-right-radius: 0;
  }

  button:not(:last-of-type) {
    border-bottom-left-radius: 0;
    border-bottom-right-radius: 0;
  }

  &:not(:first-child) {
    border-top-left-radius: 0;
    border-top-right-radius: 0;

    button:first-of-type {
      border-top-left-radius: 0;
      border-top-right-radius: 0;
    }
  }

  &:not(:last-child) {
    border-bottom-left-radius: 0;
    border-bottom-right-radius: 0;

    button:last-of-type {
      border-bottom-left-radius: 0;
      border-bottom-right-radius: 0;
    }
  }
`
